import { tr, uiLocale } from "../i18n/LanguageContext";
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Calendar as CalendarIcon, X, RotateCcw } from "lucide-react";
import { Button } from "./ui/button";
import SimpleCalendar from "./SimpleCalendar";
import { cn } from "../lib/utils";

const DateRangePicker = ({ onDateRangeChange, resetTrigger = 0, initialRange = null, className }) => {
  
  const formatDateRange = (dateRange) => {
    if (!dateRange) return tr("Все даты");
    
    const { from, to } = dateRange;
    
    if (!from) return tr("Все даты");
    
    const formatDate = (date) => {
      return date.toLocaleDateString(uiLocale(), {
        day: 'numeric',
        month: 'short'
      });
    };

    if (!to || from.getTime() === to.getTime()) {
      return formatDate(from);
    }
    
    return `${formatDate(from)} — ${formatDate(to)}`;
  };
  
  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useState(initialRange);
  const [displayText, setDisplayText] = useState(initialRange ? formatDateRange(initialRange) : tr("Все даты"));
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);

  // Initialize once from the URL-provided initialRange. This effect intentionally
  // runs only on mount: the parent (EventsPage) rebuilds `initialState` on every
  // render, so making this dependency-driven would cause stale initialRange to
  // overwrite a freshly-reset user state. URL → initial range is a one-shot
  // hydration step; ongoing changes are driven by the user via `handleRangeSelect`.
  useEffect(() => {
    if (initialRange && !range) {
      setRange(initialRange);
      setDisplayText(formatDateRange(initialRange));
    }
  }, []);

  // Reset state when resetTrigger changes
  useEffect(() => {
    if (resetTrigger > 0) {
      setRange(undefined);
      setDisplayText(tr("Все даты"));
      setIsOpen(false);
    }
  }, [resetTrigger]);

  // Close popover when clicking outside.
  // Now that the modal is rendered via Portal, the click-outside logic for the
  // popup itself is handled by the backdrop onClick. We keep this only to
  // close when the trigger button is clicked again or any non-modal area is
  // clicked. The popoverRef no longer needs to live inside the document tree
  // adjacent to the button.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Lock body scroll while the modal is open (prevents background page from
  // scrolling on iPhone Safari / Android Chrome behind the calendar).
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const handleRangeSelect = (selectedRange) => {
    setRange(selectedRange);
    
    if (selectedRange?.from) {
      const newDisplayText = formatDateRange(selectedRange);
      setDisplayText(newDisplayText);
      
      // Auto-close if we have a complete range or single date
      if (selectedRange.to || !selectedRange.from) {
        setTimeout(() => {
          setIsOpen(false);
        }, 300);
      }
      
      // Call the callback
      onDateRangeChange(selectedRange);
    }
  };

  const handleReset = (e) => {
    e.stopPropagation();
    setRange(undefined);
    setDisplayText(tr("Все даты"));
    onDateRangeChange(null);
    setIsOpen(false);
  };

  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && isOpen) {
      setIsOpen(false);
    }
  };

  // Add keyboard event listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block">
      <Button
        ref={buttonRef}
        variant="outline"
        onClick={toggleCalendar}
        aria-label="Date range picker"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={cn(
          "justify-between bg-white/80 border-emerald-200 focus:border-emerald-400 hover:bg-emerald-50/50 transition-all duration-200 h-10 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2",
          range && "pr-9",
          className
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <CalendarIcon className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          <span className="text-sm truncate font-medium text-gray-700">{displayText}</span>
        </div>
      </Button>

      {/* Reset control rendered as a sibling, NOT nested inside the trigger
          <button>. This avoids the nested-button React hydration warning while
          preserving the original visual placement (top-right inside the trigger
          area) and full keyboard accessibility. */}
      {range && (
        <span
          role="button"
          tabIndex={0}
          aria-label={tr("Сбросить выбранную дату")}
          data-testid="date-picker-reset"
          onClick={handleReset}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              handleReset(e);
            }
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-5 w-5 rounded-full cursor-pointer hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1 transition-colors"
        >
          <X className="h-3 w-3 text-emerald-600" />
        </span>
      )}

      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-200"
          role="presentation"
          data-testid="date-picker-backdrop"
          onMouseDown={(e) => {
            // Click on the backdrop (not the popup itself) closes the modal.
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          {/* Calendar Popup */}
          <div
            ref={popoverRef}
            role="dialog"
            aria-modal="true"
            aria-label={tr("Выберите диапазон дат")}
            data-testid="date-picker-modal"
            className={cn(
              "relative bg-white rounded-2xl border border-gray-200 shadow-2xl",
              "animate-in fade-in-0 zoom-in-95 duration-300",
              "p-6 w-full max-w-[420px] max-h-[90vh] overflow-auto"
            )}
            style={{
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {tr("Выберите дату")}
                </h3>
              </div>
              <Button
                size="sm" 
                variant="ghost"
                onClick={handleReset}
                className="text-gray-500 hover:bg-gray-100 text-sm px-3 py-1.5 h-auto gap-1.5 rounded-lg"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {tr("Сбросить")}
              </Button>
            </div>

            {/* Calendar */}
            <div className="calendar-container">
              <SimpleCalendar
                selected={range}
                onSelect={handleRangeSelect}
              />
            </div>

            {/* Footer with quick actions */}
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const today = new Date();
                      handleRangeSelect({ from: today, to: today });
                    }}
                    aria-label="Select today's date"
                    className="text-xs px-3 py-1.5 h-auto rounded-lg border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1"
                  >
                    {tr("Сегодня")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      handleRangeSelect({ from: tomorrow, to: tomorrow });
                    }}
                    aria-label="Select tomorrow's date"
                    className="text-xs px-3 py-1.5 h-auto rounded-lg border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1"
                  >
                    {tr("Завтра")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const today = new Date();
                      const nextWeek = new Date();
                      nextWeek.setDate(today.getDate() + 7);
                      handleRangeSelect({ from: today, to: nextWeek });
                    }}
                    aria-label="Select this week date range"
                    className="text-xs px-3 py-1.5 h-auto rounded-lg border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1"
                  >
                    {tr("Эта неделя")}
                  </Button>
                </div>
                
                {range?.from && (
                  <Button
                    size="sm"
                    onClick={() => setIsOpen(false)}
                    aria-label="Apply selected date range"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 h-auto rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1"
                  >
                    {tr("Применить")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DateRangePicker;